import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface CopyIconProps {
  /** Whether the copy action just succeeded. Flip this true and it will revert
   *  to false on its own after `revertAfterMs` (default 1500ms). */
  copied: boolean;
  /** Callback when the timer fires; lets the parent reset its own state. */
  onRevert?: () => void;
  /** Override the auto-revert timer. */
  revertAfterMs?: number;
  className?: string;
}

/**
 * Visual-only copy indicator. Pairs with any onClick that handles the actual
 * `navigator.clipboard.writeText` — keep using whatever copy logic you have,
 * just render this where the static <Copy /> used to be.
 *
 * The icon swaps to a <Check /> while `copied` is true, with a 150ms scale-in.
 * Honour-reduced-motion is automatic via the `motion-safe:` variant.
 */
export function CopyIcon({
  copied,
  onRevert,
  revertAfterMs = 1500,
  className,
}: CopyIconProps) {
  const onRevertRef = useRef(onRevert);
  onRevertRef.current = onRevert;

  // Self-reverting timer so callers don't have to wire setTimeout themselves
  // unless they want a different lifecycle.
  const [internalCopied, setInternalCopied] = useState(copied);
  useEffect(() => {
    setInternalCopied(copied);
    if (!copied) return;
    const t = window.setTimeout(() => {
      setInternalCopied(false);
      onRevertRef.current?.();
    }, revertAfterMs);
    return () => window.clearTimeout(t);
  }, [copied, revertAfterMs]);

  if (internalCopied) {
    return (
      <Check
        className={cn(
          'text-emerald-500 motion-safe:animate-in motion-safe:zoom-in-50 motion-safe:duration-150',
          className,
        )}
        aria-hidden
      />
    );
  }
  return (
    <Copy
      className={cn('transition-transform', className)}
      aria-hidden
    />
  );
}
